import React, { useState, useEffect } from 'react';
import { api, apiFetch } from '../lib/api';
import { Btn } from './ui';
import useIsMobile from '../hooks/useIsMobile';
import EmailSearch from './EmailSearch';

export default function SendModal({ sheet, onClose, onSent }) {
  const contacts = sheet.artist?.distributionContacts || [];
  const groups = sheet.artist?.distributionGroups || [];
  const fallbackList = sheet.artist?.distributionList || [];

  const [selectedGroups, setSelectedGroups] = useState(() => new Set());
  const [excludedContacts, setExcludedContacts] = useState(() => new Set());
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [extraEmails, setExtraEmails] = useState([]);
  const [oneOff, setOneOff] = useState('');
  const [sending, setSending] = useState(false);
  const [appUsers, setAppUsers] = useState([]);

  useEffect(() => {
    apiFetch('/users').then(users => {
      setAppUsers((users || []).filter(u => u.email).map(u => {
        const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ');
        // Also check distribution contacts for a name match
        const contactMatch = contacts.find(c => c.email?.toLowerCase() === u.email.toLowerCase());
        return {
          email: u.email,
          name: fullName || contactMatch?.name || u.name || null,
          photo: u.photo || null,
        };
      }));
    }).catch(() => {});
  }, [contacts]);

  // Build recipient list: group-selected contacts minus excluded, plus one-offs.
  // No implicit fallback to the artist's distributionList — only people the
  // user has explicitly checked or typed get the email.
  const getRecipients = () => {
    const emailSet = new Set();
    contacts.forEach(c => {
      if (excludedContacts.has(c.id)) return;
      const inGroup = (c.groupIds || []).some(gid => selectedGroups.has(gid));
      const isUngrouped = !c.groupIds || c.groupIds.length === 0;
      if (inGroup || (isUngrouped && selectedGroups.has('ungrouped'))) emailSet.add(c.email);
    });
    extraEmails.forEach(e => emailSet.add(e));
    return [...emailSet];
  };

  const recipientEmails = getRecipients();

  const toggleGroup = (gid) => {
    setSelectedGroups(prev => { const n = new Set(prev); if (n.has(gid)) n.delete(gid); else n.add(gid); return n; });
  };

  const addOneOff = () => {
    const email = oneOff.trim().toLowerCase();
    if (!email.includes('@')) return;
    if (!extraEmails.includes(email) && !recipientEmails.includes(email)) setExtraEmails(prev => [...prev, email]);
    setOneOff('');
  };

  const handleSend = async () => {
    setSending(true);
    try {
      // Always send explicit arrays so the backend never falls back to the
      // full distribution list. Empty arrays = nobody (button is disabled then).
      const groupIds = contacts.length > 0 ? [...selectedGroups] : [];
      await api.sendEmail(sheet.id, groupIds, extraEmails);
      onSent?.(recipientEmails.length);
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSending(false); }
  };

  const dateStr = new Date(sheet.date + 'T12:00:00').toLocaleDateString('en-US', { weekday:'short', month:'short', day:'numeric' });
  const inp = { background:'var(--card)', border:'1px solid var(--border)', borderRadius:10, padding:'9px 12px', fontSize:13, color:'var(--text)', outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };

  const isMobile = useIsMobile();

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent:'center', padding: isMobile ? 0 : 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      {!isMobile && <button onClick={onClose}
        style={{ position:'absolute', top:16, left:16, width:36, height:36, borderRadius:'50%',
          background:'rgba(255,255,255,0.12)', backdropFilter:'blur(10px)', WebkitBackdropFilter:'blur(10px)',
          border:'none', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1010 }}>
        <i className="fa-solid fa-xmark" style={{ fontSize:16, color:'#fff' }}></i>
      </button>}
      <div style={{ background:'var(--card)', borderRadius: isMobile ? '20px 20px 0 0' : 16, padding: isMobile ? '10px 0 0' : 24, width: isMobile ? '100%' : 560, maxWidth: isMobile ? '100%' : '90vw', border:'1px solid var(--border)', borderBottom: isMobile ? 'none' : undefined, maxHeight:'92vh', overflowY:'auto', paddingBottom: isMobile ? 'calc(88px + env(safe-area-inset-bottom))' : undefined }}>
        {isMobile && <div style={{ width:36, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 8px' }} />}
        <div style={{ padding: isMobile ? '0 20px 20px' : 0 }}>

        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
          <h3 style={{ fontSize:18, fontWeight:800, color:'var(--text)', margin:0 }}>
            <i className="fa-solid fa-paper-plane" style={{ marginRight:8, color:'var(--blue)' }}></i>
            Send Day Sheet
          </h3>
          <button onClick={onClose} style={{ background:'none', border:'none', fontSize:16, cursor:'pointer', color:'var(--text3)' }}>
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        <p style={{ fontSize:13, color:'var(--text2)', marginBottom:16 }}>
          {sheet.artist?.name} &middot; {dateStr}
          {sheet.emailSentAt && (
            <span style={{ color:'#fbbf24', marginLeft:8 }}>
              <i className="fa-solid fa-triangle-exclamation" style={{ fontSize:10, marginRight:4 }}></i>
              Already sent at {sheet.emailSentAt}
            </span>
          )}
        </p>

        {/* ── Groups ── */}
        {groups.length > 0 && (
          <div style={{ marginBottom:16 }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:8, textTransform:'uppercase' }}>
              <i className="fa-solid fa-folder" style={{ marginRight:4, fontSize:9 }}></i> Groups
            </p>
            <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
              {/* Group pills row */}
              <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                {groups.map(g => {
                  const members = contacts.filter(c => (c.groupIds || []).includes(g.id));
                  const activeCount = members.filter(c => !excludedContacts.has(c.id)).length;
                  const checked = selectedGroups.has(g.id);
                  const isExpanded = expandedGroup === g.id;
                  return (
                    <button key={g.id} onClick={() => toggleGroup(g.id)}
                      style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 14px', borderRadius:20, cursor:'pointer', fontFamily:'inherit', fontSize:12, fontWeight:700, border:'1.5px solid', transition:'all 0.15s', position:'relative',
                        background: checked ? 'rgba(30,64,175,0.1)' : 'transparent',
                        borderColor: checked ? 'var(--blue)' : 'var(--border)',
                        color: checked ? 'var(--blue)' : 'var(--text3)' }}>
                      {checked && <i className="fa-solid fa-check" style={{ fontSize:9 }}></i>}
                      {g.name} <span style={{ opacity:0.6 }}>({activeCount}/{members.length})</span>
                      {members.length > 0 && (
                        <i className={`fa-solid fa-chevron-${isExpanded ? 'up' : 'down'}`}
                          onClick={e => { e.stopPropagation(); setExpandedGroup(isExpanded ? null : g.id); }}
                          style={{ fontSize:8, marginLeft:2, padding:'2px 4px', color: checked ? 'var(--blue)' : 'var(--text3)' }}></i>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Expanded group members */}
              {expandedGroup && (() => {
                const members = expandedGroup === 'ungrouped'
                  ? contacts.filter(c => !c.groupIds || c.groupIds.length === 0)
                  : contacts.filter(c => (c.groupIds || []).includes(expandedGroup));
                const groupName = expandedGroup === 'ungrouped' ? 'Ungrouped' : groups.find(g => g.id === expandedGroup)?.name;
                if (!members.length) return null;
                return (
                  <div style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:10, padding:'8px 0', marginTop:4, maxHeight:170, overflowY:'auto' }}>
                    <p style={{ fontSize:10, fontWeight:700, letterSpacing:1.5, color:'var(--text3)', padding:'4px 14px 6px', margin:0, textTransform:'uppercase' }}>
                      {groupName}
                    </p>
                    {members.map(c => {
                      const included = !excludedContacts.has(c.id);
                      return (
                        <button key={c.id} onClick={() => {
                          setExcludedContacts(prev => { const n = new Set(prev); if (n.has(c.id)) n.delete(c.id); else n.add(c.id); return n; });
                        }}
                          style={{ display:'flex', alignItems:'center', gap:10, width:'100%', padding:'10px 14px', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit', textAlign:'left', minHeight:44 }}>
                          <div style={{ width:20, height:20, borderRadius:5, border: included ? '2px solid var(--blue)' : '2px solid var(--border)',
                            background: included ? 'var(--blue)' : 'transparent',
                            display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.12s' }}>
                            {included && <i className="fa-solid fa-check" style={{ fontSize:9, color:'#fff' }}></i>}
                          </div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ fontSize:13, fontWeight:600, color: included ? 'var(--text)' : 'var(--text3)', margin:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                              {c.name || c.email.split('@')[0]}
                            </p>
                            <p style={{ fontSize:11, color:'var(--text3)', margin:0 }}>{c.email}</p>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* ── Fallback: plain email list ── */}
        {contacts.length === 0 && fallbackList.length > 0 && (
          <div style={{ marginBottom:16, padding:'10px 14px', background:'var(--bg)', borderRadius:10, border:'1px solid var(--border)' }}>
            <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', margin:'0 0 6px', textTransform:'uppercase' }}>Recipients</p>
            <p style={{ fontSize:12, color:'var(--text2)', margin:0, lineHeight:1.6 }}>{fallbackList.join(', ')}</p>
          </div>
        )}

        {/* ── Ungrouped Contacts ── */}
        {(() => {
          const ungrouped = contacts.filter(c => !c.groupIds || c.groupIds.length === 0);
          if (!ungrouped.length) return null;
          return (
            <div style={{ marginBottom:12 }}>
              <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                <i className="fa-solid fa-user" style={{ marginRight:4, fontSize:9 }}></i> Individual Contacts
              </p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                {ungrouped.map(c => {
                  const isOn = selectedGroups.has('ungrouped') && !excludedContacts.has(c.id);
                  return (
                    <button key={c.id} onClick={() => {
                      if (isOn) {
                        setExcludedContacts(prev => { const n = new Set(prev); n.add(c.id); return n; });
                      } else {
                        if (!selectedGroups.has('ungrouped')) toggleGroup('ungrouped');
                        setExcludedContacts(prev => { const n = new Set(prev); n.delete(c.id); return n; });
                      }
                    }}
                      style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600, border:'1.5px solid', transition:'all 0.12s',
                        background: isOn ? 'rgba(30,64,175,0.1)' : 'transparent',
                        borderColor: isOn ? 'var(--blue)' : 'var(--border)',
                        color: isOn ? 'var(--blue)' : 'var(--text3)' }}>
                      {isOn && <i className="fa-solid fa-check" style={{ fontSize:8 }}></i>}
                      {c.name || c.email.split('@')[0]}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* ── Main Team quick-select ── */}
        {(() => {
          const allGroupEmails = new Set();
          contacts.forEach(c => { if (c.email) allGroupEmails.add(c.email.toLowerCase()); });
          const availableUsers = appUsers.filter(u => !allGroupEmails.has(u.email.toLowerCase()) && !extraEmails.includes(u.email.toLowerCase()));
          if (!availableUsers.length) return null;
          return (
            <div style={{ marginBottom:12 }}>
              <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
                <i className="fa-solid fa-user-group" style={{ marginRight:4, fontSize:9 }}></i> Main Team
              </p>
              <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
                {availableUsers.map(u => (
                  <button key={u.email} onClick={() => {
                    if (!extraEmails.includes(u.email) && !recipientEmails.includes(u.email)) setExtraEmails(prev => [...prev, u.email]);
                  }}
                    style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:16, cursor:'pointer', fontFamily:'inherit', fontSize:11, fontWeight:600,
                      background:'rgba(99,102,241,0.06)', border:'1px solid rgba(99,102,241,0.15)', color:'var(--text)', transition:'all 0.12s' }}>
                    {u.photo ? (
                      <img src={u.photo} alt="" style={{ width:16, height:16, borderRadius:'50%', objectFit:'cover' }} />
                    ) : (
                      <i className="fa-solid fa-user" style={{ fontSize:8, color:'rgba(99,102,241,0.6)' }}></i>
                    )}
                    {u.name || u.email.split('@')[0]}
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* ── One-off ── */}
        <div style={{ marginBottom:16 }}>
          <p style={{ fontSize:10, fontWeight:700, letterSpacing:2, color:'var(--text3)', marginBottom:6, textTransform:'uppercase' }}>
            <i className="fa-solid fa-plus" style={{ marginRight:4, fontSize:8 }}></i> Add one-off
          </p>
          <div style={{ display:'flex', gap:6 }}>
            <div style={{ flex:1 }}>
              <EmailSearch value={oneOff} onChange={setOneOff} onSelect={(email) => { if (!recipientEmails.includes(email)) setExtraEmails(prev => [...prev, email]); }} placeholder="Search contacts or type email..." style={{ ...inp, fontSize:12, padding:'7px 10px' }} />
            </div>
            <button onClick={addOneOff} disabled={!oneOff.trim().includes('@')}
              style={{ padding:'7px 12px', borderRadius:8, background:'var(--blue)', border:'none', color:'white', cursor:'pointer', fontSize:11, fontWeight:700, opacity: oneOff.trim().includes('@') ? 1 : 0.4 }}>
              Add
            </button>
          </div>
          {extraEmails.length > 0 && (
            <div style={{ display:'flex', flexWrap:'wrap', gap:4, marginTop:6 }}>
              {extraEmails.map(e => (
                <span key={e} style={{ display:'inline-flex', alignItems:'center', gap:3, fontSize:11, fontWeight:600, color:'var(--teal, #06B6D4)', background:'rgba(6,182,212,0.06)', border:'1px solid rgba(6,182,212,0.15)', borderRadius:14, padding:'3px 8px' }}>
                  {e}
                  <button onClick={() => setExtraEmails(prev => prev.filter(x => x !== e))} style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(6,182,212,0.5)', fontSize:9, padding:0 }}>
                    <i className="fa-solid fa-xmark"></i>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* ── Footer ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 0 0', borderTop:'1px solid var(--border)' }}>
          <p style={{ fontSize:13, color:'var(--text2)', margin:0 }}>
            <strong style={{ color:'var(--text)' }}>{recipientEmails.length}</strong> recipient{recipientEmails.length !== 1 ? 's' : ''}
            {extraEmails.length > 0 && <span style={{ color:'var(--teal, #06B6D4)', marginLeft:6, fontSize:11 }}>+{extraEmails.length} one-off</span>}
          </p>
          <div style={{ display:'flex', gap:8 }}>
            <Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn>
            <Btn variant="success" size="sm" onClick={handleSend} disabled={sending || recipientEmails.length === 0}>
              {sending ? 'Sending...' : `Send to ${recipientEmails.length}`}
            </Btn>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
